import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import {
  readListChips,
  writeListChips,
  type ListChipGroup,
  type ListChipSelection,
} from "_shared/_views/list-pages/list-chip-selection";
import type { MRT_ColumnFiltersState, MRT_SortingState } from "material-react-table";

/**
 * What a list page keeps in its URL besides its tab, which is a path segment (#6712): the chips, the
 * column filters, the sort and the page size. The search text and the page number are not part of a
 * view; a page that keeps them in the URL does so beside this.
 *
 * Each value is written only where it differs from the page's default, so the default view of a
 * list is its bare path and a list has one URL for each view.
 */
export type TableView = Readonly<{
  chips: ListChipSelection;
  columnFilters: MRT_ColumnFiltersState;
  sorting: MRT_SortingState;
  pageSize: number;
}>;

/** How one page spells its view in the URL. */
export type TableViewSpec = Readonly<{
  /** The chip groups of the tab on screen. */
  chipGroups?: readonly ListChipGroup[];
  /**
   * The column filters the URL keeps, by column id, and the search parameter each is written to:
   * the parameter the page's API takes, so the URL and the request read alike. A filter whose value
   * is a list is written once per value.
   */
  columnFilterParameters?: Readonly<Record<string, string>>;
  /** The columns the URL may name as the sort. A sort by any other column is not kept. */
  sortableColumnIds?: readonly string[];
  /** The sort with no `orderBy` in the URL. */
  defaultSorting?: MRT_SortingState;
  /** The page size with no `pageSize` in the URL; the list default when absent. */
  defaultPageSize?: number;
}>;

const ORDER_BY = "orderBy";
const ORDER_DIRECTION = "orderDir";
const PAGE_SIZE = "pageSize";

/** The view `searchParams` names, for a page spelled as `spec` says. */
export function readTableView(spec: TableViewSpec, searchParams: URLSearchParams): TableView {
  return {
    chips: readListChips(spec.chipGroups ?? [], searchParams),
    columnFilters: readColumnFilters(spec, searchParams),
    sorting: readSorting(spec, searchParams),
    pageSize: readPageSize(spec, searchParams),
  };
}

/** `searchParams` with `view` written over what the URL said of it. Every other parameter is kept. */
export function writeTableView(spec: TableViewSpec, view: TableView, searchParams: URLSearchParams): URLSearchParams {
  const next = writeListChips(spec.chipGroups ?? [], view.chips, searchParams);
  writeColumnFilters(spec, view.columnFilters, next);
  writeSorting(spec, view.sorting, next);
  writePageSize(spec, view.pageSize, next);
  return next;
}

function readColumnFilters(spec: TableViewSpec, searchParams: URLSearchParams): MRT_ColumnFiltersState {
  const filters: MRT_ColumnFiltersState = [];
  for (const [columnId, parameter] of Object.entries(spec.columnFilterParameters ?? {})) {
    const values = searchParams.getAll(parameter).filter((value) => value !== "");
    if (values.length === 0) continue;
    filters.push({ id: columnId, value: values.length === 1 ? values[0] : values });
  }
  return filters;
}

function writeColumnFilters(spec: TableViewSpec, filters: MRT_ColumnFiltersState, searchParams: URLSearchParams) {
  for (const [columnId, parameter] of Object.entries(spec.columnFilterParameters ?? {})) {
    searchParams.delete(parameter);
    const value = filters.find((filter) => filter.id === columnId)?.value;
    for (const each of Array.isArray(value) ? value : [value]) {
      if (typeof each === "string" && each !== "") searchParams.append(parameter, each);
    }
  }
}

function readSorting(spec: TableViewSpec, searchParams: URLSearchParams): MRT_SortingState {
  const named = searchParams.get(ORDER_BY)?.toLowerCase();
  const columnId = spec.sortableColumnIds?.find((id) => id.toLowerCase() === named);
  if (columnId === undefined) return spec.defaultSorting ?? [];
  const direction = searchParams.get(ORDER_DIRECTION)?.toLowerCase();
  return [{ id: columnId, desc: direction === OrderDirection.Descending.toLowerCase() }];
}

function writeSorting(spec: TableViewSpec, sorting: MRT_SortingState, searchParams: URLSearchParams) {
  searchParams.delete(ORDER_BY);
  searchParams.delete(ORDER_DIRECTION);
  const sort = sorting.find((each) => spec.sortableColumnIds?.includes(each.id));
  if (sort === undefined || isDefaultSort(spec, sort)) return;
  searchParams.set(ORDER_BY, sort.id);
  searchParams.set(ORDER_DIRECTION, sort.desc ? OrderDirection.Descending : OrderDirection.Ascending);
}

function isDefaultSort(spec: TableViewSpec, sort: { id: string; desc: boolean }): boolean {
  const defaultSort = spec.defaultSorting?.[0];
  return defaultSort !== undefined && defaultSort.id === sort.id && defaultSort.desc === sort.desc;
}

function defaultPageSize(spec: TableViewSpec): number {
  return spec.defaultPageSize ?? PaginationParameters.DEFAULT_PAGE_SIZE;
}

function readPageSize(spec: TableViewSpec, searchParams: URLSearchParams): number {
  const named = Number.parseInt(searchParams.get(PAGE_SIZE) ?? "", 10);
  return Number.isInteger(named) && named > 0 ? named : defaultPageSize(spec);
}

function writePageSize(spec: TableViewSpec, pageSize: number, searchParams: URLSearchParams) {
  if (pageSize === defaultPageSize(spec)) searchParams.delete(PAGE_SIZE);
  else searchParams.set(PAGE_SIZE, pageSize.toString());
}

/**
 * The tab a list page's path names, or the page's default tab when it names none or one the page does
 * not have. The tab is a path segment, `/fee-schedules/templates`, never a search parameter.
 */
export function readListTab<TTab extends string>(
  segment: string | undefined,
  tabs: readonly TTab[],
  defaultTab: TTab,
): TTab {
  const named = segment?.toLowerCase();
  return tabs.find((tab) => tab.toLowerCase() === named) ?? defaultTab;
}

/**
 * The path of `tab` on the list page at `basePath`. The default tab is the page's own path, with no
 * segment, so it has one URL. A tab is a different kind of thing, so nothing the other tab's chips
 * or filters said is carried over.
 */
export function listTabPath<TTab extends string>(basePath: string, tab: TTab, defaultTab: TTab): string {
  return tab === defaultTab ? basePath : `${basePath}/${tab}`;
}
